import type { RequisitionStatus } from '../interfaces/requisition.interface';
import type { B2bLcStatus, UdStatus, ExpRealizationStatus } from '../interfaces/commercial.interface';
import type { WorkOrderStatus } from '../interfaces/maintenance';
/**
 * Every status a record type can move through, with a one-line meaning. Each list is taken from the backend
 * enum and the real transitions in the named service (not guessed). The order is the usual path through the life
 * of the record, then the side exits.
 */
export interface StatusStage<T extends string = string> { value: T; label: string; meaning: string }

const s = <T extends string>(value: T, label: string, meaning: string): StatusStage<T> => ({ value, label, meaning });

/** QuotationStatus; QuotationService, SalesOrderService, SalesInvoiceService. */
export const QUOTATION_STAGES: StatusStage[] = [
    s('DRAFT', 'Draft', 'Being written. Not yet sent to the customer.'),
    s('SENT', 'Sent', 'Sent to the customer. Waiting for a yes or no.'),
    s('CONFIRMED', 'Confirmed', 'The customer accepted it. It can now be turned into an order or invoice.'),
    s('CONVERTED', 'Converted', 'Turned into a sales order or invoice. Deleting that draft or cancelling the order puts it back to Confirmed.'),
    s('REJECTED', 'Rejected', 'The customer said no. A reason is recorded.'),
    s('EXPIRED', 'Expired', 'Still Sent after its valid-until date. Set automatically.'),
];

/** SalesOrderStatus; SalesOrderService. */
export const SALES_ORDER_STAGES: StatusStage[] = [
    s('DRAFT', 'Draft', 'Being written. Nothing is reserved yet.'),
    s('CONFIRMED', 'Confirmed', 'Agreed with the customer. Nothing delivered yet.'),
    s('PARTIALLY_DELIVERED', 'Partially delivered', 'Some of the quantity has gone out on posted challans.'),
    s('DELIVERED', 'Delivered', 'All of the quantity has been delivered.'),
    s('CLOSED', 'Closed', 'Closed by a person. Any quantity still to deliver is dropped.'),
    s('CANCELLED', 'Cancelled', 'Cancelled before any delivery. Only possible from Draft or Confirmed.'),
];

/** ChallanStatus; DeliveryChallanService. */
export const CHALLAN_STAGES: StatusStage[] = [
    s('DRAFT', 'Draft', 'Being prepared. Stock has not moved.'),
    s('POSTED', 'Posted', 'Goods are out. Stock is reduced and the ledger is updated.'),
    s('CANCELLED', 'Cancelled', 'Cancelled. A posted challan is reversed first. Not allowed once it is on an invoice or has a return.'),
];

/** SalesInvoiceStatus; SalesInvoiceService. */
export const SALES_INVOICE_STAGES: StatusStage[] = [
    s('DRAFT', 'Draft', 'Being prepared. Not in the ledger.'),
    s('ISSUED', 'Issued', 'Issued to the customer and posted to the ledger. Nothing paid yet.'),
    s('PARTIALLY_PAID', 'Partially paid', 'Receipts and credits cover part of the total.'),
    s('PAID', 'Paid', 'Receipts and credits cover the whole total.'),
    s('VOIDED', 'Voided', 'Cancelled after issue. The ledger entry is reversed.'),
];

/** ReceiptStatus; ReceiptService. */
export const RECEIPT_STAGES: StatusStage[] = [
    s('POSTED', 'Posted', 'Money received and recorded in the ledger.'),
    s('REVERSED', 'Reversed', 'Undone. The ledger entry is reversed and the invoice balance is restored.'),
];

/** RequisitionStatus; RequisitionService (LEGAL_TRANSITIONS). */
export const REQUISITION_STAGES: StatusStage<RequisitionStatus>[] = [
    s('NEW', 'New', 'Raised. Waiting for an approver.'),
    s('APPROVED', 'Approved', 'Approved. Waiting for the store to issue the items.'),
    s('ISSUED', 'Issued', 'The store has issued the items. Final.'),
    s('REJECTED', 'Rejected', 'Turned down from New or Approved. Final.'),
];

/** PurchaseOrderStatus; PurchaseOrderService. */
export const PURCHASE_ORDER_STAGES: StatusStage[] = [
    s('DRAFT', 'Draft', 'Being written. Not submitted.'),
    s('PENDING_APPROVAL', 'Pending approval', 'Submitted above the approval limit, or changed after approval. Waiting for an approver.'),
    s('APPROVED', 'Approved', 'Approved. Not yet sent to the supplier.'),
    s('ORDERED', 'Ordered', 'Marked as sent to the supplier. Waiting for goods.'),
    s('PARTIALLY_RECEIVED', 'Partially received', 'Some goods have arrived on posted receipts.'),
    s('RECEIVED', 'Received', 'All goods have arrived. Some may not be billed yet.'),
    s('CLOSED', 'Closed', 'All received goods have been billed.'),
    s('REJECTED', 'Rejected', 'An approver turned it down.'),
    s('CANCELLED', 'Cancelled', 'Cancelled before any goods are received. Only possible from Draft, Pending approval, Approved or Ordered.'),
];

/** GoodsReceiptStatus; GoodsReceiptService. */
export const GOODS_RECEIPT_STAGES: StatusStage[] = [
    s('DRAFT', 'Draft', 'Being prepared. Stock has not moved.'),
    s('POSTED', 'Posted', 'Goods are in. Stock is increased and the ledger is updated.'),
    s('REVERSED', 'Reversed', 'A posted receipt that was undone. Stock and ledger are reversed.'),
];

/** SupplierBillStatus; SupplierBillService, SupplierBillSettlementService. (Draft exists in the enum but live code never sets it.) */
export const SUPPLIER_BILL_STAGES: StatusStage[] = [
    s('UNPAID', 'Unpaid', 'Recorded and ready to pay. Nothing paid yet.'),
    s('BLOCKED', 'Blocked', 'Held: it did not match the order and receipt, or it is on an approval hold. Cannot be paid until released.'),
    s('PARTIALLY_PAID', 'Partially paid', 'Payments cover part of the bill.'),
    s('PAID', 'Paid', 'Payments cover the whole bill.'),
    s('REVERSED', 'Reversed', 'Undone. The ledger entry is reversed.'),
];

/** SupplierPaymentStatus; SupplierPaymentService. */
export const SUPPLIER_PAYMENT_STAGES: StatusStage[] = [
    s('POSTED', 'Posted', 'Payment made and recorded in the ledger.'),
    s('REVERSED', 'Reversed', 'Undone. The ledger entry is reversed and the bill balance is restored.'),
];

/** ExportLcService (ALLOWED_TRANSITIONS). */
export const EXPORT_LC_STAGES: StatusStage[] = [
    s('RECEIVED', 'Received', 'Marked as received from the buyer\'s bank. Nothing shipped yet.'),
    s('PARTIALLY_SHIPPED', 'Partially shipped', 'Marked as partly shipped.'),
    s('FULLY_SHIPPED', 'Fully shipped', 'Marked as fully shipped.'),
    s('REALIZED', 'Realized', 'Marked as paid against the shipment.'),
    s('CLOSED', 'Closed', 'Marked as finished. Not allowed while any back-to-back LC is still open.'),
];

/** BackToBackLcService.deriveStatus. The first three follow the amount used. Only Closed is set by hand. */
export const B2B_LC_STAGES: StatusStage<B2bLcStatus>[] = [
    s('OPENED', 'Opened', 'Opened with the bank. Nothing used yet.'),
    s('PARTIALLY_UTILIZED', 'Partially utilized', 'Part of the amount has been used.'),
    s('FULLY_UTILIZED', 'Fully utilized', 'The whole amount has been used.'),
    s('CLOSED', 'Closed', 'Closed by a person. It stays closed.'),
];

/** UdRegisterService (ALLOWED_TRANSITIONS). */
export const UD_STAGES: StatusStage<UdStatus>[] = [
    s('DRAFT', 'Draft', 'Being prepared. Not submitted.'),
    s('SUBMITTED', 'Submitted', 'Sent for approval. Can go back to Draft.'),
    s('APPROVED', 'Approved', 'Approved. Can be used. Cannot step back while it has utilizations.'),
    s('CLOSED', 'Closed', 'Finished. Final.'),
];

/** ExpFormService.deriveStatus. The first three follow the amount realized. Written off is set by hand. */
export const EXP_FORM_STAGES: StatusStage<ExpRealizationStatus>[] = [
    s('NOT_REALIZED', 'Not realized', 'No payment received against this form yet.'),
    s('PARTIALLY_REALIZED', 'Partially realized', 'Part of the invoice value has been received.'),
    s('REALIZED', 'Realized', 'The full invoice value has been received.'),
    s('WRITTEN_OFF', 'Written off', 'Given up by a person. Only from Not realized or Partially realized. It can be reverted.'),
];

/** WorkOrderStatus; WorkOrderService (assign, start, hold, resume, complete, close, reopen, cancel). */
export const WORK_ORDER_STAGES: StatusStage<WorkOrderStatus>[] = [
    s('OPEN', 'Open', 'Raised. No technician yet.'),
    s('ASSIGNED', 'Assigned', 'A technician is assigned. Work has not started.'),
    s('IN_PROGRESS', 'In progress', 'Work has started.'),
    s('ON_HOLD', 'On hold', 'Paused with a reason, for example waiting for parts. Resume to continue.'),
    s('COMPLETED', 'Completed', 'Work is done. A manager reviews it and closes it, or reopens it. Nothing closes it automatically.'),
    s('CLOSED', 'Closed', 'Closed by a manager. Cause and action codes are required only for breakdowns, and only when that setting is on. Old closed orders may be archived and still open by link.'),
    s('CANCELLED', 'Cancelled', 'Cancelled before completion. Not allowed once parts have been issued.'),
];
